import {
  HttpErrorResponse,
  HttpInterceptorFn,
  HttpRequest,
  HttpHandlerFn,
  HttpEvent,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { StorageService } from '../services/storage.service';
import { BaseHttpService } from '../services/base-http.service';
import { ENV_CONFIG, EnvironmentConfig } from '../tokens/environment.token';
import { SiteCookies, TokenRefreshResponse } from '@chefami-monorepo/types';
import { SiteText } from '@chefami-monorepo/utils/constants';

/**
 * Thrown when the session can't be recovered: a 401, or a token refresh that
 * fails. Apps behind login can react to it (see `provideDataAccessHttp`).
 */
export class SessionExpiredError extends Error {
  constructor() {
    super(SiteText.SESSION_ERROR);
    this.name = 'SessionExpiredError';
  }
}

/**
 * errorInterceptor
 *
 * Modern functional interceptor that provides:
 * - Automatic token refresh on expired token errors
 * - Centralized HTTP error logging and handling
 * - Authentication/session timeout detection
 * - Request retry with new tokens
 */
export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const storage = inject(StorageService);
  const baseHttp = inject(BaseHttpService);
  const env = inject(ENV_CONFIG);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      // Structured error logging for debugging
      console.error('[HTTP Error Interceptor]', {
        url: req.url,
        method: req.method,
        status: error.status,
        statusText: error.statusText,
        message: error.message,
        timestamp: new Date().toISOString(),
        error,
      });

      // Handle token expiry - attempt token refresh
      if (error.status === 500 && error.error?.message === 'Expired token') {
        return handleTokenRefresh(req, next, error, storage, baseHttp, env);
      }

      // Handle authentication errors - session timeout
      if (error.status === 401) {
        console.warn(
          '[HTTP Error] Authentication failed or session expired:',
          req.url,
        );
        // Apps decide how to react (e.g. account redirects to login)
        return throwError(() => new SessionExpiredError());
      }

      // Handle network connectivity issues
      if (error.status === 0) {
        console.error('[HTTP Error] Network connectivity issue detected');
      }

      // Re-throw error for upstream handling by individual services
      return throwError(() => error);
    }),
  );
};

/**
 * Handles token refresh when expired token error is detected
 */
function handleTokenRefresh(
  originalRequest: HttpRequest<unknown>,
  next: HttpHandlerFn,
  error: HttpErrorResponse,
  storage: StorageService,
  baseHttp: BaseHttpService,
  env: EnvironmentConfig,
): Observable<HttpEvent<unknown>> {
  const refreshToken = storage.getCookie(SiteCookies.REFRESH_TOKEN);

  if (!refreshToken) {
    console.error('[Token Refresh] No refresh token available');
    return throwError(() => new SessionExpiredError());
  }

  const refreshPayload = {
    token: refreshToken,
  };

  console.warn('[Token Refresh] Attempting to refresh token...');

  return baseHttp
    .sendRequest<TokenRefreshResponse>(env.getNewToken, refreshPayload)
    .pipe(
      // Only the refresh call itself maps to a session error; errors from the
      // retried request below propagate unchanged.
      catchError((refreshError) => {
        console.error(
          '[Token Refresh] Error during token refresh:',
          refreshError,
        );
        return throwError(() => new SessionExpiredError());
      }),
      switchMap((response) => {
        if (response.response.code === '0000') {
          // Update tokens in storage
          storage.setCookie(SiteCookies.ACCESS_TOKEN, response.response.token);
          storage.setCookie(
            SiteCookies.REFRESH_TOKEN,
            response.response.refresh_token,
          );

          console.warn('[Token Refresh] Token refreshed successfully');

          // Clone the original request with new authorization header
          let updatedRequest = originalRequest.clone({
            setHeaders: {
              Authorization: `Bearer ${response.response.token}`,
            },
          });

          // Special handling for logout requests - update refresh token in body
          if (originalRequest.url.includes('logout') && originalRequest.body) {
            updatedRequest = updatedRequest.clone({
              body: {
                ...(originalRequest.body as Record<string, unknown>),
                token: response.response.refresh_token,
              },
            });
          }

          // Retry the original request with new token
          return next(updatedRequest);
        } else {
          // Token refresh failed
          console.error('[Token Refresh] Failed:', response.response.message);
          return throwError(() => new SessionExpiredError());
        }
      }),
    );
}
